<script setup>
import { computed, ref, watch } from 'vue';

import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import { TONE_CLASSES } from './liveMetrics';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  loadingMessage: {
    type: String,
    default: '',
  },
  emptyTitle: {
    type: String,
    default: '',
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
});

defineEmits(['select']);

const PAGE_SIZE = 10;
const page = ref(1);
const search = ref('');

const filteredItems = computed(() => {
  const term = search.value.trim().toLowerCase();
  if (!term) return props.items;
  return props.items.filter(item =>
    [item.name, item.subtitle].some(value =>
      value?.toLowerCase().includes(term)
    )
  );
});

const pagedItems = computed(() =>
  filteredItems.value.slice(
    (page.value - 1) * PAGE_SIZE,
    page.value * PAGE_SIZE
  )
);

watch(search, () => {
  page.value = 1;
});

watch(
  () => filteredItems.value.length,
  length => {
    const lastPage = Math.max(Math.ceil(length / PAGE_SIZE), 1);
    if (page.value > lastPage) page.value = lastPage;
  }
);
</script>

<template>
  <div class="flex w-full flex-col gap-3">
    <Input
      v-model="search"
      type="search"
      :placeholder="searchPlaceholder"
      class="w-full md:max-w-sm"
    />
    <div
      v-if="isLoading && !items.length"
      class="flex items-center justify-center p-8 text-base"
    >
      <Spinner />
      <span>{{ loadingMessage }}</span>
    </div>
    <EmptyState v-else-if="!filteredItems.length" :title="emptyTitle" />
    <template v-else>
      <div
        class="flex flex-col divide-y divide-n-weak rounded-xl outline outline-1 outline-n-container"
      >
        <button
          v-for="item in pagedItems"
          :key="item.id"
          v-tooltip="$t('OVERVIEW_REPORTS.DETAIL.VIEW')"
          type="button"
          class="group flex w-full min-w-0 items-center gap-3 px-4 py-2.5 text-left transition-colors first:rounded-t-xl last:rounded-b-xl hover:bg-n-alpha-1 rtl:text-right"
          @click="$emit('select', item)"
        >
          <Avatar
            :src="item.thumbnail"
            :name="item.name"
            :status="item.status"
            :size="28"
            hide-offline-status
            rounded-full
          />
          <div class="flex min-w-0 flex-1 flex-col">
            <span class="truncate text-sm text-n-slate-12">
              {{ item.name }}
            </span>
            <span v-if="item.subtitle" class="truncate text-xs text-n-slate-11">
              {{ item.subtitle }}
            </span>
          </div>
          <div class="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
            <span
              v-for="metric in item.metrics"
              :key="metric.key"
              class="flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs"
              :class="
                metric.value ? TONE_CLASSES[metric.tone] : TONE_CLASSES.slate
              "
            >
              <span class="hidden sm:inline">{{ metric.label }}</span>
              <span class="font-medium tabular-nums">{{ metric.value }}</span>
            </span>
          </div>
          <Icon
            icon="i-lucide-chevron-right"
            class="size-4 shrink-0 text-n-slate-10 transition-colors group-hover:text-n-slate-12 rtl:rotate-180"
          />
        </button>
      </div>
      <PaginationFooter
        v-if="filteredItems.length > PAGE_SIZE"
        class="before:hidden max-w-full"
        :current-page="page"
        :total-items="filteredItems.length"
        :items-per-page="PAGE_SIZE"
        current-page-info="OVERVIEW_REPORTS.DETAIL.SHOWING"
        @update:current-page="page = $event"
      />
    </template>
  </div>
</template>
